import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { promotionAvatar } from "../lib/api";
import AvatarLayered from "./AvatarLayered";
import { ChampionCrown, useChampionId } from "./ChampionCrown";
import { Check, Crown, Swords, X } from "lucide-react";

export const PW_COLOR = "#FF9500";
const GOLD = "#FFD700";
const CLASS_LABEL = { heavyweight: "HEAVYWEIGHT", middleweight: "MIDDLEWEIGHT", cruiserweight: "CRUISERWEIGHT", tag_team: "TAG TEAM", wildcard: "WILDCARD" };
const CLASS_COLOR = { heavyweight: "#FF007F", middleweight: "#00F0FF", cruiserweight: "#FFD700", tag_team: "#39FF14", wildcard: "#9D00FF" };
const PW_CARD_KINDS = ["pw_start", "pw_round", "pw_matchup", "pw_match", "pw_fight_win", "pw_champion"];
export const isPWCardKind = (kind) => PW_CARD_KINDS.includes(kind);

const fmt = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
};

function useNow() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 500); return () => clearInterval(t); }, []);
  return now;
}

export function PromoBadge({ promo, className = "w-10 h-10", color = PW_COLOR, testId }) {
  const nav = useNavigate();
  const champId = useChampionId();
  if (!promo?.id) return null;
  return (
    <span className={`relative inline-block shrink-0 ${className}`}>
      <img
        src={promotionAvatar(promo.id, promo.avatar_updated_at)}
        alt={promo.name}
        title={promo.name}
        data-testid={testId}
        onClick={() => nav(`/promotion/${promo.id}`)}
        className="w-full h-full rounded-md object-cover border-2 cursor-pointer hover:scale-105 transition-transform bg-[#0A051A]"
        style={{ borderColor: color, boxShadow: `0 0 10px ${color}77` }}
      />
      {champId === promo.id && <ChampionCrown className="-top-2 left-1/2 -translate-x-1/2 w-[40%] max-w-[26px] min-w-[12px] aspect-square" />}
    </span>
  );
}

function StatusChip({ show, now }) {
  if (show.status === "live") {
    return (
      <span className="inline-flex items-center gap-1 font-heading text-xs" data-testid="pw-live-indicator">
        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#39FF14", boxShadow: "0 0 6px #39FF14" }} />
        <span style={{ color: "#39FF14", textShadow: "0 0 6px #39FF14" }}>LIVE</span>
      </span>
    );
  }
  const target = show.status === "scheduled" ? show.starts_at : show.ends_at;
  return (
    <span className="font-mono text-xs" data-testid="pw-countdown" style={{ color: show.status === "open" ? "#39FF14" : PW_COLOR }}>
      {show.status === "scheduled" ? "NEXT " : "REG "}{fmt(new Date(target).getTime() - now)}
    </span>
  );
}

export function PromotionWarsCard({ show, onRegister, onOpenRoster }) {
  const now = useNow();
  const reason = show.pw_ineligible_reason;
  let label = "REGISTER PROMOTION";
  if (show.status === "scheduled") label = "OPENS AT SHOWTIME";
  else if (show.status === "live") label = "WAR IN PROGRESS";
  else if (show.filled >= show.slots) label = "FULL";
  else if (reason) label = "INELIGIBLE";
  const disabled = show.status !== "open" || !!reason || show.filled >= show.slots;
  return (
    <div data-testid="show-card-promotion_wars" className="pvp-show group relative rounded-xl p-[2px] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5"
         style={{ background: `linear-gradient(150deg, ${PW_COLOR}, #5B3BFF55, ${PW_COLOR}88)`, boxShadow: `0 0 12px ${PW_COLOR}33` }}>
     <div className="rounded-[10px] bg-[#110726]/95 p-2 h-full">
      <div className="pvp-show-img rounded-lg mb-1.5 relative overflow-hidden" style={{ border: `1px solid ${PW_COLOR}66` }}>
        <img src="/pvp/show_promotion_wars.webp" alt="Promotion Wars" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
        <div className="absolute bottom-1 right-1 px-1 py-0.5 rounded bg-black/70 backdrop-blur">
          <StatusChip show={show} now={now} />
        </div>
      </div>
      <div className="font-graffiti text-sm leading-tight mb-0.5 truncate" style={{ color: PW_COLOR, textShadow: `0 0 8px ${PW_COLOR}88` }}>PROMOTION WARS</div>
      <div className="flex items-center justify-between mb-1.5 gap-1">
        <button data-testid="show-slots-promotion_wars" onClick={() => onOpenRoster(show)} className="text-[10px] font-mono neon-cyan hover:underline">
          {show.filled}/{show.slots}
        </button>
        <span className="text-[9px] font-mono text-[#A89BCC] truncate">12:00 · 20:00 UTC</span>
      </div>
      {show.is_registered ? (
        <div data-testid="show-registered-badge-promotion_wars"
             className="w-full text-center rounded-md py-1.5 font-heading text-[10px] uppercase tracking-widest border-2 bg-[#0A051A]"
             style={{ color: "#39FF14", borderColor: "#39FF14", boxShadow: "0 0 10px rgba(57,255,20,0.35)" }}>
          <span className="inline-flex items-center gap-1"><Check size={13} strokeWidth={3} /> REGISTERED</span>
        </div>
      ) : (
        <button data-testid="show-register-promotion_wars" disabled={disabled} onClick={() => onRegister(show)}
                title={reason || ""} className="btn-neon w-full text-[10px] py-1.5">
          {label}
        </button>
      )}
      {reason && !show.is_registered && show.status !== "live" && (
        <div data-testid="pw-ineligible-reason" className="mt-1 text-[9px] font-mono text-[#FF007F] leading-tight">{reason}</div>
      )}
     </div>
    </div>
  );
}

function WrestlerStack({ members, dim, color }) {
  return (
    <div className="flex items-center -space-x-2 shrink-0">
      {(members || []).map((mm, i) => (
        <AvatarLayered
          showCrown
          key={mm.wrestler_id || i}
          fgSrc={`${api.defaults.baseURL}/wrestlers/${mm.wrestler_id}/avatar?v=${new Date(mm.avatar_updated_at || 0).getTime()}`}
          promo={mm.promotion}
          className="w-10 h-10 rounded-md border-2"
          fgClassName={dim ? "grayscale opacity-60" : ""}
          style={{ borderColor: color, boxShadow: dim ? "none" : `0 0 8px ${color}88`, zIndex: 5 - i }}
        />
      ))}
    </div>
  );
}

function MatchSide({ members, won, align, onOpenProfile }) {
  const color = won ? "#39FF14" : "#FF007F";
  return (
    <div className={`flex items-center gap-1.5 min-w-0 flex-1 ${align === "right" ? "flex-row-reverse text-right" : ""}`}>
      <WrestlerStack members={members} dim={!won} color={color} />
      <div className="min-w-0">
        <div className="font-heading text-[11px] truncate" style={{ color: won ? "#fff" : "#A89BCC" }}>
          {(members || []).map(x => x.wrestler_name).join(" & ")}
        </div>
        <div className="text-[9px] font-mono truncate">
          {(members || []).map((x, i) => (
            <button key={i} type="button" onClick={() => x.account_name && onOpenProfile(x.account_name)}
                    className="text-[#00F0FF] hover:underline mr-1">{x.account_name}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

const cardStyle = (color) => ({
  background: `linear-gradient(135deg, ${color}26, rgba(10,5,26,0.92))`,
  border: `2px solid ${color}`,
  boxShadow: `0 0 16px ${color}55`,
});

export function PWChatCard({ m, onOpenProfile }) {
  const meta = m.meta || {};
  const kind = meta.kind;
  if (kind === "pw_round") {
    return (
      <div data-testid={`pw-card-${kind}`} className="flex items-center gap-2 my-1.5">
        <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${PW_COLOR})` }} />
        <div className="font-heading text-xs tracking-[0.3em] pw-pop" style={{ color: PW_COLOR, textShadow: `0 0 8px ${PW_COLOR}` }}>{meta.label}</div>
        <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, ${PW_COLOR}, transparent)` }} />
      </div>
    );
  }
  if (kind === "pw_start") {
    return (
      <div data-testid={`pw-card-${kind}`} className="rounded-lg p-2.5 my-1 pw-pop" style={cardStyle(PW_COLOR)}>
        <div className="font-heading text-sm tracking-widest flex items-center gap-1.5" style={{ color: PW_COLOR, textShadow: `0 0 8px ${PW_COLOR}` }}>
          <Swords size={16} /> PROMOTION WARS BEGINS
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {(meta.promotions || []).map(p => <PromoBadge key={p.id} promo={p} className="w-9 h-9" />)}
        </div>
      </div>
    );
  }
  if (kind === "pw_matchup") {
    return (
      <div data-testid={`pw-card-${kind}`} className="rounded-lg p-2.5 my-1 pw-pop" style={cardStyle(PW_COLOR)}>
        <div className="text-[9px] font-heading tracking-[0.3em] text-center mb-1.5" style={{ color: PW_COLOR }}>{meta.label} · PROMOTION FIGHT</div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <PromoBadge promo={meta.a} className="w-14 h-14" color="#00F0FF" />
            <div className="font-heading text-sm truncate text-white">{meta.a?.name}</div>
          </div>
          <div className="font-heading text-2xl pw-vs" style={{ color: GOLD, textShadow: `0 0 12px ${GOLD}, 0 0 20px #FF007F` }}>VS</div>
          <div className="flex items-center gap-2 flex-1 min-w-0 flex-row-reverse text-right">
            <PromoBadge promo={meta.b} className="w-14 h-14" color="#FF007F" />
            <div className="font-heading text-sm truncate text-white">{meta.b?.name}</div>
          </div>
        </div>
      </div>
    );
  }
  if (kind === "pw_match") {
    const color = CLASS_COLOR[meta.class] || PW_COLOR;
    return (
      <div data-testid={`pw-card-${kind}`} className="rounded-lg p-2 my-1 pw-slide" style={{ background: "rgba(10,5,26,0.92)", border: `1px solid ${color}88`, boxShadow: `0 0 10px ${color}33` }}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[9px] font-heading tracking-widest px-1.5 py-0.5 rounded" style={{ background: `${color}22`, color, border: `1px solid ${color}66` }}>
            {CLASS_LABEL[meta.class]}
          </span>
          <span className="text-[9px] font-mono text-[#A89BCC] truncate ml-2">{meta.a_promo?.name} vs {meta.b_promo?.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <MatchSide members={meta.a} won={meta.winner === "a"} onOpenProfile={onOpenProfile} />
          <div className="font-heading text-lg shrink-0 px-1" data-testid="pw-match-score">
            <span style={{ color: meta.winner === "a" ? "#39FF14" : "#fff" }}>{meta.score_a}</span>
            <span className="text-[#695D8E] mx-1">-</span>
            <span style={{ color: meta.winner === "b" ? "#39FF14" : "#fff" }}>{meta.score_b}</span>
          </div>
          <MatchSide members={meta.b} won={meta.winner === "b"} align="right" onOpenProfile={onOpenProfile} />
        </div>
      </div>
    );
  }
  if (kind === "pw_fight_win") {
    return (
      <div data-testid={`pw-card-${kind}`} className="rounded-lg p-2.5 my-1 flex items-center gap-3 pw-pop" style={cardStyle("#39FF14")}>
        <PromoBadge promo={meta.winner} className="w-14 h-14" color="#39FF14" />
        <div className="min-w-0 flex-1">
          <div className="text-[9px] font-heading tracking-[0.25em] text-[#39FF14]">{meta.label === "FINAL" ? "FINAL WINNER" : "ADVANCES"}</div>
          <div className="font-heading text-lg truncate text-white" style={{ textShadow: "0 0 8px #39FF14" }}>{meta.winner?.name}</div>
          <div className="text-[10px] font-mono text-[#A89BCC] truncate">defeat {meta.loser?.name}</div>
        </div>
        <div className="font-heading text-3xl shrink-0" style={{ color: GOLD, textShadow: `0 0 10px ${GOLD}` }}>{meta.score_w}-{meta.score_l}</div>
      </div>
    );
  }
  if (kind === "pw_champion") {
    return (
      <div data-testid={`pw-card-${kind}`} className="rounded-lg p-3 my-1.5 flex items-center gap-3 relative overflow-hidden pw-champion" style={cardStyle(GOLD)}>
        <div className="absolute inset-0 pointer-events-none pw-shine" />
        <div className="relative shrink-0">
          <Crown size={22} className="absolute -top-3 left-1/2 -translate-x-1/2 z-10" style={{ color: GOLD, filter: `drop-shadow(0 0 6px ${GOLD})` }} fill={GOLD} />
          <PromoBadge promo={meta.promotion} className="w-[72px] h-[72px]" color={GOLD} testId="pw-champion-avatar" />
        </div>
        <div className="min-w-0 flex-1 relative">
          <div className="text-[10px] font-heading tracking-[0.3em]" style={{ color: GOLD }}>PROMOTION WARS CHAMPIONS</div>
          <div className="font-heading text-2xl truncate text-white" style={{ textShadow: `0 0 10px ${GOLD}, 0 0 22px #FF007F` }} data-testid="pw-champion-name">{meta.promotion?.name}</div>
          <div className="text-[10px] font-mono text-[#39FF14]">+{meta.hof_points} Promotion HOF points · {meta.participants} promotions</div>
        </div>
      </div>
    );
  }
  return null;
}

function FightRow({ f }) {
  if (f.bye) {
    return (
      <div className="flex items-center gap-2 p-2 rounded-md bg-[#0A051A] border border-[#2D1C52] text-[11px] font-mono">
        <PromoBadge promo={f.bye} className="w-8 h-8" />
        <span className="text-white">{f.bye.name}</span>
        <span className="text-[#A89BCC]">advances with a bye</span>
      </div>
    );
  }
  const aWon = f.winner_id === f.a.id;
  return (
    <div className="p-2 rounded-md bg-[#0A051A] border border-[#2D1C52]" data-testid="pw-fight-row">
      <div className="flex items-center gap-2">
        <PromoBadge promo={f.a} className="w-8 h-8" color={aWon ? "#39FF14" : "#FF007F"} />
        <span className="font-heading text-xs truncate flex-1" style={{ color: aWon ? "#39FF14" : "#A89BCC" }}>{f.a.name}</span>
        <span className="font-heading text-sm" style={{ color: GOLD }}>{f.score_a}-{f.score_b}</span>
        <span className="font-heading text-xs truncate flex-1 text-right" style={{ color: !aWon ? "#39FF14" : "#A89BCC" }}>{f.b.name}</span>
        <PromoBadge promo={f.b} className="w-8 h-8" color={!aWon ? "#39FF14" : "#FF007F"} />
      </div>
      <div className="mt-1.5 grid gap-0.5">
        {(f.matches || []).map((mt, i) => (
          <div key={i} className="flex items-center gap-2 text-[10px] font-mono">
            <span className="w-24 shrink-0" style={{ color: CLASS_COLOR[mt.class] }}>{CLASS_LABEL[mt.class]}</span>
            <span className={`truncate flex-1 ${mt.winner === "a" ? "text-white" : "text-[#695D8E]"}`}>{mt.a.map(x => x.wrestler_name).join(" & ")}</span>
            <span className="text-[#695D8E]">vs</span>
            <span className={`truncate flex-1 text-right ${mt.winner === "b" ? "text-white" : "text-[#695D8E]"}`}>{mt.b.map(x => x.wrestler_name).join(" & ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PromotionWarsModal({ show, onClose }) {
  const [regs, setRegs] = useState(null);
  const results = show.results;
  useEffect(() => {
    if (results?.rounds) return;
    api.get(`/shows/${show.show_id}/roster`).then(r => setRegs(r.data.registered || [])).catch(() => setRegs([]));
  }, [show.show_id, results?.rounds]);
  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" data-testid="pw-modal" onClick={onClose}>
      <div className="card-surface p-4 w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} style={{ borderColor: `${PW_COLOR}88` }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-base uppercase tracking-widest" style={{ color: PW_COLOR }}>Promotion Wars</h3>
          <button data-testid="pw-modal-close" onClick={onClose} className="p-1 rounded-lg border border-[#FF007F]/40 text-[#FF007F] hover:bg-[#FF007F]/10"><X size={16} /></button>
        </div>
        {results?.rounds ? (
          <>
            <div className="flex flex-col items-center mb-4" data-testid="pw-modal-winner">
              <Crown size={26} style={{ color: GOLD, filter: `drop-shadow(0 0 8px ${GOLD})` }} fill={GOLD} />
              <PromoBadge promo={results.winner} className="w-24 h-24" color={GOLD} />
              <div className="font-heading text-2xl mt-2 text-white" style={{ textShadow: `0 0 10px ${GOLD}` }}>{results.winner?.name}</div>
              <div className="text-[10px] font-mono text-[#39FF14]">Champions · +100 Promotion HOF points</div>
            </div>
            {results.rounds.map(r => (
              <div key={r.round} className="mb-3">
                <div className="text-[10px] font-heading tracking-[0.3em] mb-1.5" style={{ color: PW_COLOR }}>{r.label}</div>
                <div className="flex flex-col gap-1.5">{r.fights.map((f, i) => <FightRow key={i} f={f} />)}</div>
              </div>
            ))}
          </>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="pw-modal-registered">
            {Array.from({ length: show.slots || 8 }).map((_, i) => {
              const r = (regs || [])[i];
              return (
                <div key={i} className="rounded-md p-2 bg-[#0A051A] border flex flex-col items-center gap-1 min-h-[110px] justify-center"
                     style={{ borderColor: r ? `${PW_COLOR}88` : "#2D1C52", borderStyle: r ? "solid" : "dashed" }}>
                  {r ? (
                    <>
                      <PromoBadge promo={{ id: r.promotion_id, name: r.promotion_name, avatar_updated_at: r.avatar_updated_at }} className="w-14 h-14" />
                      <div className="font-heading text-[11px] text-white text-center truncate w-full">{r.promotion_name}</div>
                      <div className="text-[9px] font-mono text-[#A89BCC]">{r.member_count} members</div>
                    </>
                  ) : (
                    <span className="text-[10px] font-mono text-[#695D8E]">Open slot</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
